import React, { useEffect, useState } from 'react';
import { Helix2000Character } from '../../../shared/CharacterTypes';
import { CharacterClass } from '../../../shared/CharacterTypes';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { comboPointState } from '../../systems/combat/ComboPointSystem';
import { CLASS_RESOURCE } from '../../../config/ui/classResource.config';
import {
  THROWING_STAR_MAGAZINE,
  THROWING_STAR_RELOAD_MS,
} from '../../../shared/combat/throwingStars';
import { getThrowingStarRackDiagnostics } from '../../systems/player/abilities/throwingStars/ThrowingStarRack';
import { HUD_Z_INDEX } from '../../../config/ui/hudDefaults.config';
import { COLORS, FONT_SIZE, FONT_WEIGHT, FONTS, LETTER_SPACING } from '../theme';
import { resolveToCharacterClass } from '../../../shared/classes/CallingDisplayRegistry';

interface StarCounterProps {
  player: Helix2000Character | null;
}

interface RackSnapshot {
  remaining: number;
  max: number;
  reloading: boolean;
  readyAt: number;
  reloadRemainingMs: number;
}

// ── Layout & Sizing Constants (No Magic Numbers) ───────────────────────────
const SHURIKEN_PIP_SIZE = 16;
const COMBO_PIP_SIZE = 14;
const RELOAD_BAR_WIDTH_PX = 136;
const RELOAD_BAR_HEIGHT_PX = 16;
/** Sits comfortably above the 88px actionbar + buff row stack */
const RESOURCE_BAR_BOTTOM_OFFSET_PX = 120;

// ── Thematic Colors ────────────────────────────────────────────────────────
const PALETTE = {
  background: 'rgba(12, 16, 26, 0.90)',
  border: 'rgba(255, 255, 255, 0.14)',
  divider: 'rgba(255, 255, 255, 0.12)',
  label: '#94a3b8',
  starActive: '#e2e8f0',
  starSpent: '#1a2230',
  starStroke: '#38bdf8',
  starSpentStroke: '#334155',
  starGlow: 'rgba(56, 189, 248, 0.75)',
  reloadBorder: '#ea580c',
  reloadGradientStart: '#c2410c',
  reloadGradientEnd: '#f97316',
  reloadText: COLORS.textPrimary,
  comboActive: '#a855f7',
  comboActiveStroke: '#c084fc',
  comboSpent: '#1c152b',
  comboSpentStroke: '#3e2e5c',
  comboMax: '#f59e0b',
  comboMaxStroke: '#fde047',
} as const;

/** 4-pointed metallic shuriken matching ThrowingStarShape.ts geometry */
export const ShurikenPip: React.FC<{ active: boolean; size?: number }> = ({ active, size = SHURIKEN_PIP_SIZE }) => (
  <svg
    width={size}
    height={size}
    viewBox="-12 -12 24 24"
    style={{
      filter: active ? `drop-shadow(0 0 3px ${PALETTE.starGlow})` : 'none',
      transition: 'all 0.15s ease-out',
    }}
  >
    <path
      d="M 0,-11 L 4.5,-4.5 L 1.8,-1.2 L 11,0 L 4.5,4.5 L 1.2,1.8 L 0,11 L -4.5,4.5 L -1.8,1.2 L -11,0 L -4.5,-4.5 L -1.2,-1.8 Z"
      fill={active ? PALETTE.starActive : PALETTE.starSpent}
      stroke={active ? PALETTE.starStroke : PALETTE.starSpentStroke}
      strokeWidth="1"
    />
    <circle
      cx="0"
      cy="0"
      r="2.5"
      fill={active ? '#0f172a' : '#0f131a'}
      stroke={active ? '#7dd3fc' : '#475569'}
      strokeWidth="1"
    />
  </svg>
);

/** Diamond combo pip for rogue finishers */
export const ComboPip: React.FC<{ active: boolean; isMaxed: boolean; size?: number }> = ({
  active,
  isMaxed,
  size = COMBO_PIP_SIZE,
}) => {
  const fill = isMaxed ? PALETTE.comboMax : active ? PALETTE.comboActive : PALETTE.comboSpent;
  const stroke = isMaxed ? PALETTE.comboMaxStroke : active ? PALETTE.comboActiveStroke : PALETTE.comboSpentStroke;
  const shadow = isMaxed
    ? 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.9))'
    : active
      ? 'drop-shadow(0 0 2px rgba(168, 85, 247, 0.6))'
      : 'none';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      style={{
        filter: shadow,
        transition: 'all 0.15s ease-out',
        transform: isMaxed ? 'scale(1.12)' : 'scale(1)',
      }}
    >
      <polygon
        points="8,1 15,8 8,15 1,8"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.2"
      />
    </svg>
  );
};

/**
 * Combat Resource Bar for The Unseen (Rogue).
 *
 * Surfaces the real executing mechanics of the class:
 * 1. Throwing Star Magazine: 7 shurikens, depletes per throw, 1.2s reload with countdown bar.
 * 2. Combo Points: 0-5 finisher pips, built by builders, consumed by finishers (eviscerate, etc.).
 */
export const StarCounter: React.FC<StarCounterProps> = ({ player }) => {
  const [combo, setCombo] = useState<{ points: number; max: number }>(() => {
    const s = comboPointState.read();
    return { points: s.points, max: s.max || CLASS_RESOURCE.maxPoints };
  });

  const [rack, setRack] = useState<RackSnapshot>(() => {
    if (!player) {
      return {
        remaining: THROWING_STAR_MAGAZINE,
        max: THROWING_STAR_MAGAZINE,
        reloading: false,
        readyAt: 0,
        reloadRemainingMs: 0,
      };
    }
    const diag = getThrowingStarRackDiagnostics(player);
    return {
      remaining: diag.remaining,
      max: THROWING_STAR_MAGAZINE,
      reloading: diag.reloading,
      readyAt: diag.readyAt,
      reloadRemainingMs: diag.reloadRemainingMs,
    };
  });

  const [reloadProgress, setReloadProgress] = useState(0);
  const [reloadSecondsLeft, setReloadSecondsLeft] = useState(0);

  // Subscribe to live combat updates
  useEffect(() => {
    const bus = EventBus.getInstance();

    const unsubCombo = bus.on(
      EVENTS.COMBO_POINTS_CHANGED,
      (p: { points: number; max: number }) => setCombo({ points: p.points, max: p.max }),
    );

    const unsubRack = bus.on(
      EVENTS.THROWING_STARS_CHANGED,
      (payload) => {
        if (!player || (payload.playerId && payload.playerId !== player.id)) return;
        setRack({
          remaining: payload.remaining,
          max: payload.max,
          reloading: payload.reloading,
          readyAt: payload.readyAt,
          reloadRemainingMs: payload.reloadRemainingMs,
        });
      },
    );

    const unsubVfx = bus.on('vfx:spawn', (vfx: any) => {
      if (vfx.effectId === 'reload_stars' && (!player || vfx.casterId === player.id)) {
        const readyAt = Date.now() + (vfx.duration ?? THROWING_STAR_RELOAD_MS);
        setRack((prev) => ({
          ...prev,
          remaining: 0,
          reloading: true,
          readyAt,
          reloadRemainingMs: vfx.duration ?? THROWING_STAR_RELOAD_MS,
        }));
      } else if (vfx.effectId === 'reload_stars_complete' && (!player || vfx.casterId === player.id)) {
        setRack((prev) => ({
          ...prev,
          remaining: THROWING_STAR_MAGAZINE,
          reloading: false,
          readyAt: 0,
          reloadRemainingMs: 0,
        }));
      }
    });

    return () => {
      unsubCombo();
      unsubRack();
      unsubVfx();
    };
  }, [player]);

  // Smooth rAF loop during the 1.2s reload window
  useEffect(() => {
    if (!rack.reloading || rack.readyAt <= 0) {
      setReloadProgress(0);
      setReloadSecondsLeft(0);
      return;
    }

    let animId: number;
    const startAt = rack.readyAt - THROWING_STAR_RELOAD_MS;

    const tick = () => {
      const now = Date.now();
      const remainingMs = Math.max(0, rack.readyAt - now);
      const elapsed = Math.max(0, now - startAt);
      const frac = Math.min(1, elapsed / THROWING_STAR_RELOAD_MS);
      setReloadProgress(frac);
      setReloadSecondsLeft(remainingMs / 1000);

      if (now >= rack.readyAt) {
        setRack((prev) => ({
          ...prev,
          remaining: THROWING_STAR_MAGAZINE,
          reloading: false,
          readyAt: 0,
          reloadRemainingMs: 0,
        }));
        setReloadProgress(1);
        setReloadSecondsLeft(0);
      } else {
        animId = requestAnimationFrame(tick);
      }
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [rack.reloading, rack.readyAt]);

  if (!player) return null;
  const isUnseen = player.class === 'rogue' || resolveToCharacterClass(player.class) === CharacterClass.ROGUE;
  if (!isUnseen) return null;

  const maxCombo = combo.max || CLASS_RESOURCE.maxPoints;
  const comboPoints = combo.points;
  const isMaxed = comboPoints >= maxCombo;

  return (
    <div
      data-testid="unseen-resource-bar"
      style={{
        position: 'absolute',
        bottom: `${RESOURCE_BAR_BOTTOM_OFFSET_PX}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        background: PALETTE.background,
        backdropFilter: 'blur(8px)',
        border: `1px solid ${PALETTE.border}`,
        borderRadius: '10px',
        padding: '6px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        zIndex: HUD_Z_INDEX.actionbar,
        pointerEvents: 'none',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
        userSelect: 'none',
      }}
    >
      {/* ── Left: Throwing Star Ammo & Reload ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span
            style={{
              color: PALETTE.label,
              fontSize: `${FONT_SIZE.xs}px`,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}
          >
            Stars
          </span>
          <span
            data-testid="star-magazine-count"
            style={{
              color: rack.reloading ? PALETTE.reloadGradientEnd : PALETTE.starStroke,
              fontSize: `${FONT_SIZE.xs}px`,
              fontWeight: FONT_WEIGHT.bold,
              fontFamily: FONTS.mono,
            }}
          >
            {rack.reloading ? 'RELOADING' : `${rack.remaining}/${rack.max}`}
          </span>
        </div>

        {rack.reloading ? (
          <div
            data-testid="star-reload-progress"
            style={{
              width: `${RELOAD_BAR_WIDTH_PX}px`,
              height: `${RELOAD_BAR_HEIGHT_PX}px`,
              background: 'rgba(0, 0, 0, 0.65)',
              border: `1px solid ${PALETTE.reloadBorder}`,
              borderRadius: '4px',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: `${reloadProgress * 100}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${PALETTE.reloadGradientStart}, ${PALETTE.reloadGradientEnd})`,
                transition: 'width 0.05s linear',
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '100%',
                textAlign: 'center',
                fontSize: `${FONT_SIZE.xs - 1}px`,
                fontWeight: FONT_WEIGHT.bold,
                color: PALETTE.reloadText,
                textShadow: '0 1px 2px #000',
                letterSpacing: '0.5px',
              }}
            >
              {reloadSecondsLeft > 0 ? `${reloadSecondsLeft.toFixed(1)}s` : 'READY'}
            </span>
          </div>
        ) : (
          <div data-testid="star-pips-row" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {Array.from({ length: rack.max }, (_, i) => (
              <ShurikenPip key={i} active={i < rack.remaining} size={SHURIKEN_PIP_SIZE} />
            ))}
          </div>
        )}
      </div>

      {/* ── Divider ── */}
      <div
        style={{
          width: '1px',
          height: '24px',
          background: PALETTE.divider,
        }}
      />

      {/* ── Right: Combo Points (Finisher Currency) ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span
            style={{
              color: PALETTE.label,
              fontSize: `${FONT_SIZE.xs}px`,
              fontWeight: FONT_WEIGHT.bold,
              letterSpacing: LETTER_SPACING.wide,
              textTransform: 'uppercase',
            }}
          >
            Combo
          </span>
          <span
            data-testid="combo-points-count"
            style={{
              color: isMaxed ? PALETTE.comboMax : PALETTE.comboActiveStroke,
              fontSize: `${FONT_SIZE.xs}px`,
              fontWeight: FONT_WEIGHT.bold,
              fontFamily: FONTS.mono,
            }}
          >
            {isMaxed ? 'MAX' : `${comboPoints}/${maxCombo}`}
          </span>
        </div>
        <div data-testid="combo-pips-row" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {Array.from({ length: maxCombo }, (_, i) => (
            <ComboPip key={i} active={i < comboPoints} isMaxed={isMaxed} size={COMBO_PIP_SIZE} />
          ))}
        </div>
      </div>
    </div>
  );
};
